import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import OwButton from "../../shared/OwButton/OwButton";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import OwTitle from "../../shared/OwTitle/OwTitle";
import IOrder from "../../../models/IOrder";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IUpdateOrderMetaDataDTO from "../../../models/DTO/IUpdateOrderMetaDataDTO";
import IOnUpdateOrderMetaDataDTO from "../../../models/DTO/IOnUpdateOrderMetaDataDTO";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import "./OrderMetadataSidebar.scss";
import { Socket } from "socket.io-client";
import UiContext from "../../../contexts/UiContext";
import { SocketContext } from "../../../contexts/SocketContext";
import OwDateInput from "../../shared/OwDateInput/OwDateInput";
import { Moment } from "moment-timezone";

type Props = {
    cancelFn: (refreshData?: boolean) => void;
    orderModel: IOrder;
};

export const OrderMetadataSidebar: React.FC<Props> = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;

    const [saveActive, setSaveActive] = useState(false);
    const [orderModel, setOrderModel] = useState(props.orderModel);

    useEffect(() => {
        socket.on(EndpointsEnum.OrderOnUpdateMetaData, onUpdateMetaData);

        return () => {
            socket.removeListener(EndpointsEnum.OrderOnUpdateMetaData, onUpdateMetaData);
        };
    }, []);

    const cancel = () => {
        props.cancelFn();
    };

    const save = () => {
        updateMetaData();
    };

    const updateMetaData = () => {
        const payload: IUpdateOrderMetaDataDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid: orderModel.guid,
            orderModel: {},
        };

        if (orderModel.name !== props.orderModel.name) {
            payload.orderModel.name = orderModel.name;
        }

        if (orderModel.fishbowl_so_id !== props.orderModel.fishbowl_so_id) {
            payload.orderModel.fishbowl_so_id = orderModel.fishbowl_so_id;
        }

        if (orderModel.qbd_TxnID !== props.orderModel.qbd_TxnID) {
            payload.orderModel.qbd_TxnID = orderModel.qbd_TxnID;
        }

        if (orderModel.qbd_Invoice_TxnID !== props.orderModel.qbd_Invoice_TxnID) {
            payload.orderModel.qbd_Invoice_TxnID = orderModel.qbd_Invoice_TxnID;
        }

        if (orderModel.created_at !== props.orderModel.created_at) {
            payload.orderModel.created_at = orderModel.created_at;
        }

        socket.emit(EndpointsEnum.OrderUpdateMetaData, payload);
        setSaveActive(true);
    };

    const onUpdateMetaData = (data: IOnUpdateOrderMetaDataDTO) => {
        const notification: INotification = {
            message: "successfully_updated_order_metadata",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        setSaveActive(false);
        props.cancelFn(true);
    };

    const handleDateChange = (date: Moment) => {
        const selectedDate = date.format("YYYY-MM-DDT00:00:00Z");

        const updatedOrderModel = {
            ...orderModel,
            created_at: selectedDate,
        };

        setOrderModel(updatedOrderModel);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        const updatedOrderModel = {
            ...orderModel,
            [name]: value,
        };

        setOrderModel(updatedOrderModel);
    };

    const resetQbdRef = (type: string) => {
        const updatedOrderModel = {
            ...orderModel,
        };

        switch (type) {
            case "sales_order":
                // @ts-expect-error
                updatedOrderModel.qbd_TxnID = null;
                break;

            case "invoice":
                // @ts-expect-error
                updatedOrderModel.qbd_Invoice_TxnID = null;
                break;
        }

        setOrderModel(updatedOrderModel);
    };

    const getFishbowlItems = () => {
        if (!supplierModel.Supplier_Integration.has_fishbowl) {
            return null;
        }

        return (
            <div className={"mt-5"}>
                <div>
                    <OwLabel title={"fishbowl_so"} />
                    <OwInputWrapper name={"fishbowl_so_id"} fn={handleChange} value={orderModel.fishbowl_so_id} />
                </div>
            </div>
        );
    };

    const getQbdItems = () => {
        if (!supplierModel.Supplier_Integration.has_qbd) {
            return null;
        }

        return (
            <div className={"mt-5"}>
                <div>
                    <div>
                        <OwLabel title={"qbd_sales_order_ref"} />
                    </div>
                    {props.orderModel.qbd_TxnID ? (
                        <>
                            <div className={"text-small text-muted"}>{props.orderModel.qbd_TxnID}</div>
                            <OwButton
                                className={"btn-danger"}
                                title={"reset"}
                                fn={() => {
                                    resetQbdRef("sales_order");
                                }}
                            />
                        </>
                    ) : (
                        <OwLabel className={"text-warning"} title={"not_set"} />
                    )}
                    {props.orderModel.qbd_TxnID !== orderModel.qbd_TxnID ? (
                        <OwLabel title={"marked_for_reset"} className={"ms-3 text-success"} />
                    ) : null}
                </div>

                <div className={"mt-5"}>
                    <div>
                        <OwLabel title={"qbd_invoice_ref"} />
                    </div>
                    {props.orderModel.qbd_Invoice_TxnID ? (
                        <>
                            <div className={"text-small text-muted"}>{props.orderModel.qbd_Invoice_TxnID}</div>
                            <OwButton
                                className={"btn-danger"}
                                title={"reset"}
                                fn={() => {
                                    resetQbdRef("invoice");
                                }}
                            />
                        </>
                    ) : (
                        <OwLabel className={"text-warning"} title={"not_set"} />
                    )}
                    {props.orderModel.qbd_Invoice_TxnID !== orderModel.qbd_Invoice_TxnID ? (
                        <OwLabel title={"marked_for_reset"} className={"ms-3 text-success"} />
                    ) : null}
                </div>
            </div>
        );
    };

    return (
        <div className={"OrderMetadataSidebar p-2"}>
            <div className={"col-12"}>
                <div className="text-end">
                    <button
                        className="btn btn-danger btn-sm"
                        onClick={() => {
                            cancel();
                        }}>
                        <i className="far fa-times" />
                    </button>
                </div>
                <OwTitle className={"mb-2"} leftIconName={""} title={"edit_metadata"} />
                <hr />
            </div>

            <div className={"col-12"}>
                <div className={"mt-5 mb-5"}>
                    <OwLabel title={"warning_edit_metadata"} className={"text-warning"} leftIconClass={"fas fa-exclamation-triangle"} />
                </div>
                <div className={""}>
                    <OwLabel title={"name"} />
                    <OwInputWrapper name={"name"} fn={handleChange} value={orderModel.name} />
                </div>
                <div className={"mt-5"}>
                    <OwLabel title={"created_at"} />
                    <OwDateInput selectedDate={orderModel.created_at} fn={handleDateChange} />
                </div>
                {getQbdItems()}
                {getFishbowlItems()}
            </div>

            <div className={"col-12 mt-5"}>
                <OwButton title={"save"} fn={save} active={saveActive} disableOnActive={true} />
            </div>
        </div>
    );
};
